<template>
    <!-- Main area -->
    <main class="min-w-0 flex-1 border-t border-taupe-200 lg:flex">
        <!-- Right of the flag list on large screens. The trusted layout skips the overlay notice on flags screens. -->
        <div class="flex min-h-0 min-w-0 flex-1 flex-col">
            <environment-overlay-banner
                v-if="routeProjectKey && routeEnvironmentKey"
                :project-key="routeProjectKey"
                :environment-key="routeEnvironmentKey"
            />

            <div
                v-if="hasSelection"
                class="flex min-h-0 min-w-0 flex-1 flex-col"
            >
                <router-view />
            </div>

            <div
                v-if="!hasSelection"
                class="min-h-0 w-full flex-1 overflow-y-auto"
            >
                <div>
                    <!-- No flags exist yet -->
                    <zen-empty-state
                        v-if="!loading && flags.length === 0"
                        title="Create your first flag"
                        description="Feature flags let you control what your users see without deploying new code. Create a flag, connect your SDK, and start shipping with confidence."
                        :primary-action="
                            can(PERMISSIONS.FLAG_CREATE, permissionScope)
                                ? {
                                      label: 'Create a flag',
                                      handler: openCreateModal,
                                  }
                                : null
                        "
                    >
                        <template #icon>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1"
                                stroke="currentColor"
                                class="w-20 h-20"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M6 13.5V3.75m0 9.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V16.5m12-3V3.75m0 9.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V16.5m-6-9V3.75m0 3.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 9.75V10.5"
                                />
                            </svg>
                        </template>
                    </zen-empty-state>

                    <!-- Flags exist, none selected — only meaningful on lg+ where list is a sidebar -->
                    <zen-empty-state
                        v-else-if="!loading && flags.length > 0"
                        class="hidden lg:block"
                        title="Select a flag to get started"
                        description="Pick a flag from the list on the left to view its configuration, targeting rules, and evaluation history."
                    >
                        <template #icon>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1"
                                stroke="currentColor"
                                class="w-20 h-20"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M6 13.5V3.75m0 9.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V16.5m12-3V3.75m0 9.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V16.5m-6-9V3.75m0 3.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 9.75V10.5"
                                />
                            </svg>
                        </template>
                    </zen-empty-state>
                </div>
            </div>
        </div>

        <!-- Secondary column (hidden on smaller screens) -->
        <aside
            :class="hasSelection ? 'hidden' : 'w-full'"
            class="lg:w-auto lg:order-first lg:block lg:shrink-0 overflow-y-auto"
        >
            <div
                class="relative flex h-full w-full lg:w-96 flex-col overflow-y-auto lg:border-r lg:border-taupe-200 bg-taupe-50"
            >
                <nav
                    ref="flagListScrollContainer"
                    class="h-full overflow-y-auto"
                    aria-label="Flag List"
                >
                    <div class="relative">
                        <div
                            class="sticky top-0 z-10 border-b border-taupe-200 bg-taupe-50 p-3 text-base font-medium text-taupe-500"
                        >
                            <div class="relative">
                                <!--
                                    The browser's own clear button on a search
                                    input only shows on hover/focus (and not at
                                    all in Firefox), so it is hidden here and
                                    replaced with one that stays visible for as
                                    long as the filter has a value.
                                -->
                                <input
                                    ref="flagFilterInput"
                                    type="search"
                                    v-model="filter"
                                    class="block w-full rounded-lg border-taupe-300 bg-white font-light focus:border-taupe-500 focus:ring-taupe-500 sm:text-sm p-2 [&::-webkit-search-cancel-button]:appearance-none"
                                    :class="{ 'pr-9': filter }"
                                    placeholder="Search flags..."
                                    aria-label="Search flags"
                                />
                                <button
                                    v-if="filter"
                                    type="button"
                                    data-testid="clear-flag-filter"
                                    class="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-2.5 text-taupe-400 hover:text-taupe-600 focus:text-taupe-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500"
                                    @click="clearFilter"
                                >
                                    <span class="sr-only">Clear search</span>
                                    <svg
                                        class="h-4 w-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="2"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <zen-loading-indicator v-if="loading" />

                        <ul
                            v-if="!loading"
                            class="relative z-0 divide-y divide-taupe-200"
                        >
                            <li
                                v-for="result in filteredFlags"
                                :key="result.key"
                                class="bg-white"
                            >
                                <router-link
                                    :to="{
                                        name: 'ProjectFlagDetailScreen',
                                        params: {
                                            projectKey: current.projectKey,
                                            environmentKey:
                                                current.environmentKey,
                                            key: result.key,
                                        },
                                    }"
                                    :data-flag-key="result.key"
                                    class="relative flex items-center space-x-3 cursor-pointer px-6 py-5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-sky-500"
                                    :class="
                                        hasSelection &&
                                        result.key === selectedKey
                                            ? 'bg-sky-100 hover:bg-sky-200'
                                            : 'bg-white hover:bg-taupe-200'
                                    "
                                >
                                    <div class="min-w-0 flex-1">
                                        <div class="focus:outline-hidden">
                                            <!-- Extend touch target to entire panel -->
                                            <span
                                                class="absolute inset-0"
                                                aria-hidden="true"
                                            ></span>
                                            <div
                                                class="flex items-center justify-center"
                                            >
                                                <div class="w-3/4">
                                                    <p
                                                        class="text-sm font-medium text-taupe-800"
                                                    >
                                                        {{ result.name }}
                                                    </p>
                                                    <p
                                                        class="truncate text-sm text-taupe-500 font-mono"
                                                    >
                                                        {{ result.key }}
                                                    </p>
                                                </div>
                                                <div class="w-1/4">
                                                    <svg
                                                        v-if="
                                                            result.type ==
                                                            'boolean'
                                                        "
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="inline-block float-right h-8 w-8 text-taupe-500"
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1"
                                                        stroke="currentColor"
                                                        fill="none"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    >
                                                        <path
                                                            stroke="none"
                                                            d="M0 0h24v24H0z"
                                                            fill="none"
                                                        />
                                                        <path
                                                            d="M3 17h2.397a5 5 0 0 0 4.096 -2.133l.177 -.253m3.66 -5.227l.177 -.254a5 5 0 0 1 4.096 -2.133h3.397"
                                                        />
                                                        <path
                                                            d="M18 4l3 3l-3 3"
                                                        />
                                                        <path
                                                            d="M3 7h2.397a5 5 0 0 1 4.096 2.133l4.014 5.734a5 5 0 0 0 4.096 2.133h3.397"
                                                        />
                                                        <path
                                                            d="M18 20l3 -3l-3 -3"
                                                        />
                                                    </svg>
                                                    <svg
                                                        v-if="
                                                            result.type ==
                                                            'string'
                                                        "
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="inline-block float-right h-8 w-8 text-taupe-500"
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1"
                                                        stroke="currentColor"
                                                        fill="none"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    >
                                                        <path
                                                            stroke="none"
                                                            d="M0 0h24v24H0z"
                                                            fill="none"
                                                        />
                                                        <path
                                                            d="M3 16v-6a2 2 0 1 1 4 0v6"
                                                        />
                                                        <path d="M3 13h4" />
                                                        <path
                                                            d="M10 8v6a2 2 0 1 0 4 0v-1a2 2 0 1 0 -4 0v1"
                                                        />
                                                        <path
                                                            d="M20.732 12a2 2 0 0 0 -3.732 1v1a2 2 0 0 0 3.726 1.01"
                                                        />
                                                    </svg>
                                                    <svg
                                                        v-if="
                                                            result.type ==
                                                            'number'
                                                        "
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="inline-block float-right h-8 w-8 text-taupe-500"
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                        fill="none"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    >
                                                        <path
                                                            stroke="none"
                                                            d="M0 0h24v24H0z"
                                                            fill="none"
                                                        />
                                                        <path
                                                            d="M3 10l2 -2v8"
                                                        />
                                                        <path
                                                            d="M9 8h3a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 0 -1 1v2a1 1 0 0 0 1 1h3"
                                                        />
                                                        <path
                                                            d="M17 8h2.5a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1 -1.5 1.5h-1.5h1.5a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1 -1.5 1.5h-2.5"
                                                        />
                                                    </svg>
                                                    <svg
                                                        v-if="
                                                            result.type ==
                                                            'json'
                                                        "
                                                        data-testid="flag-type-icon-json"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="inline-block float-right h-8 w-8 text-taupe-500"
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                        fill="none"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    >
                                                        <path
                                                            stroke="none"
                                                            d="M0 0h24v24H0z"
                                                            fill="none"
                                                        />
                                                        <path
                                                            d="M7 4a2 2 0 0 0 -2 2v3a2 3 0 0 1 -2 3a2 3 0 0 1 2 3v3a2 2 0 0 0 2 2"
                                                        />
                                                        <path
                                                            d="M17 4a2 2 0 0 1 2 2v3a2 3 0 0 0 2 3a2 3 0 0 0 -2 3v3a2 2 0 0 1 -2 2"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </router-link>
                            </li>
                        </ul>
                        <!-- Sticky Create Flag Button -->
                        <div
                            v-if="can(PERMISSIONS.FLAG_CREATE, permissionScope)"
                            class="sticky bottom-0 z-20 border-t border-taupe-200 bg-taupe-50/60 backdrop-blur-[2px] px-6 py-5"
                            data-tour="create-flag"
                        >
                            <div class="text-center">
                                <zen-button
                                    @click="openCreateModal"
                                    variant="secondary"
                                >
                                    Create a Flag
                                </zen-button>
                            </div>
                        </div>
                    </div>
                </nav>
            </div>
        </aside>

        <create-flag-slide-in
            :is-open="modals.create.show"
            v-model:data="modals.create.data"
            :error="modals.create.error"
            :show-alternate-type-selector="showAlternateTypeSelector"
            :type-options="typeOptions"
            :flag-name-to-slug="flagNameToSlug"
            :check-flag-slug="checkFlagSlug"
            @close="toggleCreateModal"
            @confirm="confirmCreate()"
        />
    </main>
</template>

<script>
import stringToKey from '@/helpers/string-to-key'
import { useSessionStore } from '@/stores/session'
import { isFeatureEnabled } from '@/utils/feature-flags'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import CreateFlagSlideIn from '@/screens/trusted/project/flags/modals/CreateFlagSlideIn.vue'
import EnvironmentOverlayBanner from '@/screens/_components/EnvironmentOverlayBanner.vue'

import FlagService from '@/services/flag.service'

// The trusted layout keys its <router-view> on the full route (including the
// selected flag's `key` param), so selecting a flag fully unmounts and
// remounts this screen. These module-scoped values survive that remount so
// the flag list's scroll position and search filter can be carried over to
// the new instance, and a newly created flag can still be scrolled into view
// once. The saved values are tagged with the project/environment they belong
// to so switching projects doesn't apply a stale scroll offset or filter
// from a differently sized list.
let savedScrollState = { projectKey: '', environmentKey: '', scrollTop: 0 }
let savedFilterState = { projectKey: '', environmentKey: '', filter: '' }
let pendingNewFlagScrollKey = ''

const FLAG_TYPE_OPTIONS = [
    {
        value: 'boolean',
        iconPaths: [
            'M3 17h2.397a5 5 0 0 0 4.096 -2.133l.177 -.253m3.66 -5.227l.177 -.254a5 5 0 0 1 4.096 -2.133h3.397',
            'M18 4l3 3l-3 3',
            'M3 7h2.397a5 5 0 0 1 4.096 2.133l4.014 5.734a5 5 0 0 0 4.096 2.133h3.397',
            'M18 20l3 -3l-3 -3',
        ],
    },
    {
        value: 'string',
        iconPaths: [
            'M3 16v-6a2 2 0 1 1 4 0v6',
            'M3 13h4',
            'M10 8v6a2 2 0 1 0 4 0v-1a2 2 0 1 0 -4 0v1',
            'M20.732 12a2 2 0 0 0 -3.732 1v1a2 2 0 0 0 3.726 1.01',
        ],
    },
    {
        value: 'number',
        iconPaths: [
            'M3 10l2 -2v8',
            'M9 8h3a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-2a1 1 0 0 0 -1 1v2a1 1 0 0 0 1 1h3',
            'M17 8h2.5a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1 -1.5 1.5h-1.5h1.5a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1 -1.5 1.5h-2.5',
        ],
    },
    {
        value: 'json',
        iconPaths: [
            'M7 4a2 2 0 0 0 -2 2v3a2 3 0 0 1 -2 3a2 3 0 0 1 2 3v3a2 2 0 0 0 2 2',
            'M17 4a2 2 0 0 1 2 2v3a2 3 0 0 0 2 3a2 3 0 0 0 -2 3v3a2 2 0 0 1 -2 2',
        ],
    },
]

export default {
    name: 'ProjectFlagsScreen',
    components: { CreateFlagSlideIn, EnvironmentOverlayBanner },
    setup() {
        const { can } = usePermissions()
        const { current } = useCurrentEnvironment()
        return { can, PERMISSIONS, current }
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
        routeProjectKey() {
            return this.$route.params.projectKey ?? null
        },
        routeEnvironmentKey() {
            return this.$route.params.environmentKey ?? null
        },
        currentEnvironment() {
            if (this.sessionStore.data === null) {
                return {
                    projectKey: null,
                    environmentKey: null,
                    flags: [],
                }
            }

            return this.sessionStore.data.current
        },
        permissionScope() {
            return {
                projectUlid: this.current.projectUlid,
                environmentUlid: this.current.environmentUlid,
            }
        },
        hasSelection() {
            return this.selectedKey !== undefined
        },
        selectedKey() {
            return this.$route.params.key
        },
        filteredFlags() {
            if (this.flags == null || this.flags.length === 0) {
                return []
            }

            return this.flags
                .filter((flag) =>
                    flag.name.toLowerCase().includes(this.filter.toLowerCase())
                )
                .sort((a, b) => a.name.localeCompare(b.name))
        },
        flags() {
            if (this.sessionStore.data === null) {
                return []
            }

            return this.sessionStore.data.current?.flags ?? []
        },
        showAlternateTypeSelector() {
            return this.showAlternateTypeSelectorValue
        },
        // The json type is only offered while the account is in the
        // `allow-json-flags` rollout (the same gate ZEN-718's JSON value
        // editor uses). Existing json flags still render everywhere
        // regardless, so a flag created via the API is never invisible.
        typeOptions() {
            if (this.jsonFlagsEnabledValue) {
                return FLAG_TYPE_OPTIONS
            }

            return FLAG_TYPE_OPTIONS.filter((option) => option.value !== 'json')
        },
    },
    data() {
        return {
            showAlternateTypeSelectorValue: true,
            jsonFlagsEnabledValue: false,
            loading: false,
            filter: '',
            modals: {
                create: {
                    show: false,
                    error: null,
                    flagKeyNotChanged: true,
                    data: {
                        name: null,
                        key: null,
                        description: null,
                        type: 'boolean',
                        include_in_mobile: true,
                        include_in_client: true,
                    },
                },
            },
        }
    },
    methods: {
        syncCurrentToRoute() {
            if (!this.sessionStore.data?.current) {
                return Promise.resolve(false)
            }

            if (!this.routeProjectKey || !this.routeEnvironmentKey) {
                return Promise.resolve(false)
            }

            const current = this.sessionStore.data.current
            if (
                current.projectKey === this.routeProjectKey &&
                current.environmentKey === this.routeEnvironmentKey
            ) {
                return Promise.resolve(false)
            }

            return this.sessionStore
                .setCurrent({
                    projectKey: this.routeProjectKey,
                    environmentKey: this.routeEnvironmentKey,
                    flags: [],
                })
                .then(() => true)
        },
        checkFlagSlug() {
            this.modals.create.flagKeyNotChanged =
                this.modals.create.data.key ===
                stringToKey(this.modals.create.data.name)
        },
        handleTypeOptionKeydown(event) {
            const directions = new Map([
                ['ArrowRight', 1],
                ['ArrowDown', 1],
                ['ArrowLeft', -1],
                ['ArrowUp', -1],
            ])

            const step = directions.get(event.key)
            if (!step) {
                return
            }

            event.preventDefault()

            const options = this.typeOptions
            const currentIndex = options.findIndex(
                (option) => option.value === this.modals.create.data.type
            )
            const nextIndex =
                (currentIndex + step + options.length) % options.length
            const nextOption = options.at(nextIndex)

            this.modals.create.data.type = nextOption.value

            this.$nextTick(() => {
                const group = this.$refs.typeOptionGroup
                const nextButton = group
                    ?.querySelectorAll('[role="radio"]')
                    .item(nextIndex)
                nextButton?.focus()
            })
        },
        async confirmCreate() {
            if (!this.currentEnvironment?.projectKey) {
                return
            }

            const payload = {
                ...this.modals.create.data,
            }

            const { projectKey, environmentKey } = this.currentEnvironment

            try {
                const created = await FlagService.create(projectKey, payload)
                const key = this.modals.create.data.key
                this.toggleCreateModal()

                // Insert the new flag into the cached list in place. A full
                // refresh would set loading and hide the list.
                this.sessionStore.upsertFlag(created.data)
                pendingNewFlagScrollKey = key
                await this.$router.push({
                    name: 'ProjectFlagDetailScreen',
                    params: {
                        projectKey,
                        environmentKey,
                        key,
                    },
                })
            } catch (error) {
                this.modals.create.error =
                    (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                    error.message ||
                    error.toString()
            }
        },
        openCreateModal() {
            this.toggleCreateModal()
        },
        refresh(force = false) {
            if (!force && this.flags != null && this.flags.length > 0) {
                return Promise.resolve()
            }
            if (!this.currentEnvironment?.projectKey) {
                this.loading = false
                return Promise.resolve()
            }

            this.loading = true
            return this.sessionStore
                .refreshFlags(this.currentEnvironment.projectKey)
                .catch(() => {
                    // Request is already handled globally (e.g. auth redirect)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        flagNameToSlug() {
            if (this.modals.create.flagKeyNotChanged) {
                this.modals.create.data.key = stringToKey(
                    this.modals.create.data.name
                )
            }
        },
        toggleCreateModal() {
            this.modals.create.show = !this.modals.create.show
            if (!this.modals.create.show) {
                this.modals.create.data.name = null
                this.modals.create.data.key = null
                this.modals.create.data.description = null
                this.modals.create.data.type = null
                this.modals.create.data.include_in_mobile = true
                this.modals.create.data.include_in_client = true
                this.modals.create.error = null
            }
        },
        async loadFeatureFlags() {
            const [showAlternateTypeSelector, jsonFlagsEnabled] =
                await Promise.all([
                    isFeatureEnabled('show-alternate-type-selector', true),
                    isFeatureEnabled('allow-json-flags', false),
                ])
            this.showAlternateTypeSelectorValue = showAlternateTypeSelector
            this.jsonFlagsEnabledValue = jsonFlagsEnabled
        },
        async initializeFlagsScreen() {
            const routeChanged = await this.syncCurrentToRoute()
            this.refresh(routeChanged)

            if (
                pendingNewFlagScrollKey !== '' &&
                pendingNewFlagScrollKey === this.selectedKey
            ) {
                pendingNewFlagScrollKey = ''
                this.scheduleScrollToSelectedFlag()
            }
        },
        // Identifies the project/environment this screen instance belongs
        // to, in the shape savedScrollState/savedFilterState are tagged
        // with, so a saved value is only ever restored into a matching
        // project/environment.
        currentProjectEnvironmentKey() {
            return {
                projectKey: this.currentEnvironment?.projectKey ?? '',
                environmentKey: this.currentEnvironment?.environmentKey ?? '',
            }
        },
        matchesCurrentProjectEnvironment(saved) {
            const { projectKey, environmentKey } =
                this.currentProjectEnvironmentKey()
            return (
                saved.projectKey === projectKey &&
                saved.environmentKey === environmentKey
            )
        },
        restoreFilter() {
            this.filter = this.matchesCurrentProjectEnvironment(
                savedFilterState
            )
                ? savedFilterState.filter
                : ''
        },
        // Clears the search filter and puts the cursor back in the input, so
        // the user can type a new search straight away. The clear button
        // unmounts as soon as the filter is empty, which would otherwise drop
        // keyboard focus onto the page.
        clearFilter() {
            this.filter = ''
            this.$refs.flagFilterInput?.focus()
        },
        restoreScrollPosition() {
            const container = this.$refs.flagListScrollContainer
            if (!container) {
                return
            }

            container.scrollTop = this.matchesCurrentProjectEnvironment(
                savedScrollState
            )
                ? savedScrollState.scrollTop
                : 0
        },
        scheduleScrollToSelectedFlag() {
            this.$nextTick(() => {
                this.scrollSelectedFlagIntoView()
            })
        },
        scrollSelectedFlagIntoView() {
            if (!this.hasSelection || !this.selectedKey) {
                return
            }

            const container = this.$refs.flagListScrollContainer
            if (
                !container ||
                typeof container.querySelectorAll !== 'function'
            ) {
                return
            }

            const selectedElement = [
                ...container.querySelectorAll('[data-flag-key]'),
            ].find((element) => element.dataset.flagKey === this.selectedKey)

            if (!selectedElement) {
                return
            }

            const containerRect = container.getBoundingClientRect()
            const selectedRect = selectedElement.getBoundingClientRect()
            const isFullyVisible =
                selectedRect.top >= containerRect.top &&
                selectedRect.bottom <= containerRect.bottom

            if (!isFullyVisible) {
                selectedElement.scrollIntoView({ block: 'nearest' })
            }
        },
    },
    watch: {
        routeProjectKey() {
            this.initializeFlagsScreen()
        },
        routeEnvironmentKey() {
            this.initializeFlagsScreen()
        },
    },
    created() {
        this.restoreFilter()
    },
    mounted() {
        this.restoreScrollPosition()
        this.initializeFlagsScreen()
        this.loadFeatureFlags()
    },
    beforeUnmount() {
        const { projectKey, environmentKey } =
            this.currentProjectEnvironmentKey()

        const container = this.$refs.flagListScrollContainer
        if (container) {
            savedScrollState = {
                projectKey,
                environmentKey,
                scrollTop: container.scrollTop,
            }
        }
        savedFilterState = { projectKey, environmentKey, filter: this.filter }
    },
}
</script>
